import type { ExecResult, Kernel, Output } from "./Kernel";
import { errorLabel, trimOutputs } from "./outputs";

/** Минимальный nbformat, с которым работает рабочее место. */
export type NbCell = { id: string; cell_type: "code" | "markdown" | "raw"; source: string[] | string; metadata: Record<string, unknown>; outputs?: Output[]; execution_count?: number | null };
export type Notebook = { cells: NbCell[]; metadata: Record<string, unknown>; nbformat: number; nbformat_minor: number };
export type Task = { n: number; title: string; cell_id: string; checks_cell_id: string };
export type TaskStatus = "passed" | "failed" | "idle";
export type TaskResult = { status: TaskStatus; error?: string };

export const src = (c: NbCell) => (Array.isArray(c.source) ? c.source.join("") : c.source);

export function cellById(nb: Notebook, id: string) {
  return nb.cells.find((c) => c.id === id);
}

/** Ячейки кода до первой задачи (setup): исполняются один раз за сессию ядра. */
export function setupCells(nb: Notebook, tasks: Task[]): NbCell[] {
  const first = new Set(tasks.flatMap((t) => [t.cell_id, t.checks_cell_id]));
  const out: NbCell[] = [];
  for (const c of nb.cells) {
    if (first.has(c.id)) break;
    if (c.cell_type === "code") out.push(c);
  }
  return out;
}

export type TaskRun = { status: TaskStatus; error?: string; taskOutputs: Output[]; checkOutputs: Output[]; durationMs: number };

/** Исполняет задачу: сначала её код, затем проверки. Статус — по проверкам. */
export class TaskRunner {
  private setupDone = false;
  constructor(private kernel: Kernel) {}

  reset() {
    this.setupDone = false;
  }

  private async ensureSetup(nb: Notebook, tasks: Task[]): Promise<ExecResult | null> {
    if (this.setupDone) return null;
    for (const c of setupCells(nb, tasks)) {
      const r = await this.kernel.execute(src(c));
      if (!r.ok) return r;
    }
    this.setupDone = true;
    return null;
  }

  async runTask(nb: Notebook, tasks: Task[], task: Task): Promise<TaskRun> {
    const setupErr = await this.ensureSetup(nb, tasks);
    if (setupErr) return { status: "failed", error: errorLabel(setupErr.outputs) ?? "ошибка в ячейке подготовки", taskOutputs: trimOutputs(setupErr.outputs), checkOutputs: [], durationMs: setupErr.durationMs };
    const code = cellById(nb, task.cell_id);
    const checks = cellById(nb, task.checks_cell_id);
    if (!code || !checks) return { status: "failed", error: "в ноутбуке нет ячеек этой задачи", taskOutputs: [], checkOutputs: [], durationMs: 0 };
    const r1 = await this.kernel.execute(src(code));
    if (!r1.ok) return { status: "failed", error: errorLabel(r1.outputs), taskOutputs: trimOutputs(r1.outputs), checkOutputs: [], durationMs: r1.durationMs };
    const r2 = await this.kernel.execute(src(checks));
    return {
      status: r2.ok ? "passed" : "failed",
      error: r2.ok ? undefined : errorLabel(r2.outputs),
      taskOutputs: trimOutputs(r1.outputs),
      checkOutputs: trimOutputs(r2.outputs),
      durationMs: r1.durationMs + r2.durationMs,
    };
  }

  async runAll(nb: Notebook, tasks: Task[], onTask?: (t: Task, r: TaskRun) => void): Promise<Record<string, TaskRun>> {
    const out: Record<string, TaskRun> = {};
    for (const t of tasks) {
      const r = await this.runTask(nb, tasks, t);
      out[String(t.n)] = r;
      onTask?.(t, r);
    }
    return out;
  }
}

/** Записывает выводы запуска в ячейки ноутбука (для черновика). */
export function applyRun(nb: Notebook, task: Task, run: TaskRun, executionCount: number): Notebook {
  return {
    ...nb,
    cells: nb.cells.map((c) => {
      if (c.id === task.cell_id) return { ...c, outputs: run.taskOutputs, execution_count: executionCount };
      if (c.id === task.checks_cell_id) return { ...c, outputs: run.checkOutputs, execution_count: run.checkOutputs.length || run.status === "passed" ? executionCount + 1 : null };
      return c;
    }),
  };
}

/** Итог по задачам для футера S5: «упало 2, не запускалось 1». */
export function summarize(tasks: Task[], results: Record<string, TaskResult>) {
  let passed = 0;
  let failed = 0;
  let idle = 0;
  for (const t of tasks) {
    const s = results[String(t.n)]?.status ?? "idle";
    if (s === "passed") passed++;
    else if (s === "failed") failed++;
    else idle++;
  }
  return { passed, failed, idle, total: tasks.length };
}
